// Dana AI ops (Platform Operations Console P3). LLM usage/cost/error observability
// recorded per call (Anthropic response usage → ai_usage_events). Charts via recharts.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Button, Space, Select, Table, Typography, message, Empty, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, LineChart, BarChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/console';
import { getAiUsage, type AiUsage, type ModelRow, type FeatureRow, type TenantRow, type KeySourceRow } from '../../services/aiOpsService';

const { Text } = Typography;
const ORANGE = '#F28A3A';
const BLUE = '#2563EB';
const RED = '#DC2626';

const usd = (v: number) => `$${(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const num = (v: number) => (v ?? 0).toLocaleString();

export const DanaAiOpsPage: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true); else setRefreshing(true);
    try {
      setData(await getAiUsage(days));
    } catch {
      if (initial) message.error('Could not load AI usage');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);
  useEffect(() => { void load(true); }, [load]);

  const t = data?.totals;
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #eee)" />;

  const modelCols: ColumnsType<ModelRow> = [
    { title: 'Model', dataIndex: 'model', render: (m: string) => <Text code>{m}</Text> },
    { title: 'Calls', dataIndex: 'calls', align: 'right' as const, render: num },
    { title: 'Tokens', dataIndex: 'tokens', align: 'right' as const, render: num },
    { title: 'Cost', dataIndex: 'cost_usd', align: 'right' as const, render: usd },
  ];
  const featureCols: ColumnsType<FeatureRow> = [
    { title: 'Feature', dataIndex: 'feature', render: (f: string) => <Tag>{f}</Tag> },
    { title: 'Calls', dataIndex: 'calls', align: 'right' as const, render: num },
    { title: 'Tokens', dataIndex: 'tokens', align: 'right' as const, render: num },
    { title: 'Cost', dataIndex: 'cost_usd', align: 'right' as const, render: usd },
  ];
  const tenantCols: ColumnsType<TenantRow> = [
    { title: 'Tenant', dataIndex: 'tenant_id', render: (t: string | null) => (t ? <Text code>{t}</Text> : <Text type="secondary">no tenant</Text>) },
    { title: 'Calls', dataIndex: 'calls', align: 'right' as const, render: num },
    { title: 'Cost', dataIndex: 'cost_usd', align: 'right' as const, render: usd },
  ];
  // Who paid for the call: BYOK tenant key, the platform's managed shared key, or
  // the service fallback env key. 'unknown' = rows recorded before attribution.
  const KEY_SOURCE_LABEL: Record<string, { label: string; color?: string }> = {
    tenant: { label: 'Tenant (BYOK)', color: 'blue' },
    platform: { label: 'Platform (managed)', color: 'gold' },
    env: { label: 'Service (env)', color: 'default' },
    unknown: { label: 'Unknown (legacy)' },
  };
  const keySourceCols: ColumnsType<KeySourceRow> = [
    {
      title: 'Funded by', dataIndex: 'key_source',
      render: (k: string) => {
        const m = KEY_SOURCE_LABEL[k] || { label: k };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    { title: 'Calls', dataIndex: 'calls', align: 'right' as const, render: num },
    { title: 'Tokens', dataIndex: 'tokens', align: 'right' as const, render: num },
    { title: 'Cost', dataIndex: 'cost_usd', align: 'right' as const, render: usd },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Dana AI ops"
        subtitle="LLM usage, cost and errors across the platform (recorded per call)"
        actions={
          <Space>
            <Select
              value={days} onChange={setDays} style={{ width: 150 }}
              options={[{ value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }]}
            />
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>Refresh</Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card loading={loading}><Statistic title="Cost (USD)" value={t ? usd(t.cost_usd) : '—'} /></Card></Col>
        <Col xs={12} md={6}><Card loading={loading}><Statistic title="Calls" value={t ? num(t.calls) : 0} /></Card></Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic title="Tokens" value={t ? num(t.input_tokens + t.output_tokens) : 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="Error rate"
              value={t ? `${(t.error_rate * 100).toFixed(1)}%` : '0%'}
              valueStyle={t && t.error_rate > 0.02 ? { color: 'var(--color-danger)' } : undefined}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Cost & errors over time" style={{ marginBottom: 16 }} loading={loading}>
        {!data || data.by_day.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No usage recorded yet" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.by_day} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="day" fontSize={12} />
              <YAxis yAxisId="l" fontSize={12} />
              <YAxis yAxisId="r" orientation="right" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line yAxisId="l" type="monotone" dataKey="cost_usd" name="Cost (USD)" stroke={ORANGE} strokeWidth={2} dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="errors" name="Errors" stroke={RED} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Calls per day" style={{ marginBottom: 16 }} loading={loading}>
        {!data || data.by_day.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.by_day} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="day" fontSize={12} />
              <YAxis fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="calls" name="Calls" fill={BLUE} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="By model" loading={loading}>
            <Table rowKey="model" size="small" pagination={false} dataSource={data?.by_model || []} columns={modelCols} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="By feature" loading={loading}>
            <Table rowKey="feature" size="small" pagination={false} dataSource={data?.by_feature || []} columns={featureCols} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Funded by (key source)" loading={loading}>
            <Table rowKey="key_source" size="small" pagination={false} dataSource={data?.by_key_source || []} columns={keySourceCols} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Top tenants by cost" loading={loading}>
            <Table rowKey={(r) => r.tenant_id || 'none'} size="small" pagination={false} dataSource={data?.top_tenants || []} columns={tenantCols} />
          </Card>
        </Col>
      </Row>

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Cost is estimated from per-model list prices. Covers all Dana LLM calls — assistant, skill router, insights, and
        the worksheet / timesheet / migration-mapping importers. RAG embeddings are excluded (separate provider/cost).
      </Text>
    </div>
  );
};

export default DanaAiOpsPage;
